import { LitElement, html, css } from 'lit'
import { StoreMixin } from '../store.js'
import { api } from '../api.js'
import { toast } from './app-toast.js'

// Agent subscription management (§7.5). Agent-only (route guards). An agent can
// subscribe to every inbound ticket, or CC themselves on specific client orgs.
// Each toggle maps to POST/DELETE /subscriptions.
class PageSubscriptions extends StoreMixin(LitElement) {
  static properties = {
    _clients: { state: true },
    _subs: { state: true },
    _loading: { state: true },
    _busy: { state: true }
  }

  static styles = css`
    :host { display: block; }
    h1 { color: var(--bx-brand); margin: 0 0 0.25rem; font-size: 1.4rem; }
    p.lede { color: var(--bx-muted); margin: 0 0 1rem; }
    .card { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            box-shadow: var(--bx-shadow); overflow: hidden; margin-bottom: 1.5rem; }
    .row { display: grid; grid-template-columns: 1fr auto; gap: 0.5rem 1rem; align-items: center;
           padding: 0.85rem 1rem; border-bottom: 1px solid var(--bx-border); }
    .row:last-child { border-bottom: none; }
    .name { font-weight: 600; color: var(--bx-ink); }
    .domains { color: var(--bx-muted); font-size: 0.85rem; margin-top: 0.15rem; }
    .hint { color: var(--bx-muted); font-size: 0.85rem; margin-top: 0.15rem; }
    button.toggle { font: inherit; padding: 0.4rem 0.9rem; border: 1px solid var(--bx-border);
             border-radius: var(--bx-radius); background: var(--bx-surface); color: var(--bx-ink); cursor: pointer;
             white-space: nowrap; }
    button.toggle.on { background: var(--bx-brand); color: var(--bx-brand-ink); border-color: var(--bx-brand); }
    button.toggle:disabled { opacity: 0.5; cursor: default; }
    h2 { font-size: 0.95rem; color: var(--bx-ink); margin: 0 0 0.5rem; }
    .empty, .loading { padding: 3rem; text-align: center; color: var(--bx-muted); }
  `

  constructor() {
    super()
    this._clients = []
    this._subs = []
    this._loading = true
    this._busy = false
  }

  connectedCallback() {
    super.connectedCallback()
    this._load()
  }

  async _load() {
    this._loading = true
    try {
      const [clients, { subscriptions }] = await Promise.all([
        api.get('/clients'),
        api.get('/subscriptions')
      ])
      this._clients = clients
      this._subs = subscriptions
    } catch (err) {
      toast(err.message || 'Failed to load subscriptions', 'error')
    } finally {
      this._loading = false
    }
  }

  get _allInboundSub() { return this._subs.find(s => s.scope === 'all_inbound') || null }
  _clientSub(clientId) { return this._subs.find(s => s.scope === 'client' && s.clientId === clientId) || null }

  async _toggle(existing, body) {
    if (this._busy) return
    this._busy = true
    try {
      if (existing) {
        await api.delete(`/subscriptions/${existing.id}`)
        this._subs = this._subs.filter(s => s.id !== existing.id)
        toast('Unsubscribed', 'success')
      } else {
        const sub = await api.post('/subscriptions', body)
        this._subs = [...this._subs, sub]
        toast('Subscribed', 'success')
      }
    } catch (err) {
      toast(err.message || 'Failed to update subscription', 'error')
    } finally {
      this._busy = false
    }
  }

  render() {
    if (this._loading) return html`<div class="loading">Loading…</div>`
    const all = this._allInboundSub
    return html`
      <h1>Subscriptions</h1>
      <p class="lede">Get emailed on new tickets. Subscribe to everything inbound, or CC yourself on the clients you follow.</p>

      <div class="card">
        <div class="row">
          <div>
            <div class="name">All inbound</div>
            <div class="hint">Every new ticket across all clients.</div>
          </div>
          <button class="toggle ${all ? 'on' : ''}" ?disabled=${this._busy}
            @click=${() => this._toggle(all, { scope: 'all_inbound' })}>
            ${all ? '✓ Subscribed' : 'Subscribe'}
          </button>
        </div>
      </div>

      <h2>By client</h2>
      ${this._clients.length ? html`
        <div class="card">
          ${this._clients.map(c => {
            const sub = this._clientSub(c.id)
            return html`
              <div class="row">
                <div>
                  <div class="name">${c.name}</div>
                  <div class="domains">${c.domains?.length ? c.domains.map(d => d.domain).join(', ') : 'No domains'}</div>
                </div>
                <button class="toggle ${sub ? 'on' : ''}" ?disabled=${this._busy || !!all}
                  title=${all ? 'Already covered by All inbound' : ''}
                  @click=${() => this._toggle(sub, { scope: 'client', clientId: c.id })}>
                  ${sub ? '✓ Subscribed' : 'Subscribe'}
                </button>
              </div>`
          })}
        </div>` : html`<div class="empty">No client organizations yet.</div>`}
    `
  }
}

customElements.define('page-subscriptions', PageSubscriptions)
